import 'twin.macro';
import 'styled-components/macro';
import { gql } from '@apollo/client';
import { differenceInSeconds } from 'date-fns';
import { useEffect, useState } from 'react';

import { Button } from 'play/atoms/button/Button';
import { LoadingCounter } from 'play/atoms/loading-counter/LoadingCounter';
import { LoadingMessage } from 'play/atoms/loading-message/LoadingMessage';
import { LoadingProgressBar } from 'play/atoms/loading-progress-bar/LoadingProgressBar';
import { TrackLoadingIndicator } from 'play/molecules/track-loading-indicator/TrackLoadingIndicator';
import { useTrackAmplitudeEvent } from 'support/amplitude/amplitudeEvent';
import {
  Challenge,
  ChallengeStatus,
  Track,
  TrackStatus,
  useSetupChallengeMutation,
  useTrackAndChallengeStatusQuery,
} from 'types/graphql';

import { useNotificationContext } from '../../../core/context/Notifications';

const DEFAULT_POLL_INTERVAL = 2000;

gql`
  query TrackAndChallengeStatus($trackID: String!, $challengeID: String!, $userID: String, $inviteID: String) {
    track(trackID: $trackID, userID: $userID, inviteID: $inviteID) {
      id
      status
      creating
    }
    challenge(challengeID: $challengeID, userID: $userID, inviteID: $inviteID) {
      id
      status
      failMessage
    }
  }
`;

gql`
  mutation SetupChallenge($challengeID: String!) {
    setupChallenge(challengeID: $challengeID) {
      id
    }
  }
`;

export interface StartChallengePropTypes {
  track: Pick<Track, 'id' | 'creating' | 'median_starttime'>;
  challenge: Pick<Challenge, 'id' | 'index'>;
  userID?: string | null;
  inviteID?: string | null;
  starting?: boolean;
  onClickStart: () => void;
}

export function StartChallenge({
  track,
  challenge,
  userID = null,
  inviteID = null,
  starting,
  onClickStart,
}: StartChallengePropTypes) {
  const { dispatch } = useNotificationContext();
  const trackAmplitudeEvent = useTrackAmplitudeEvent();

  const { median_starttime } = track;
  const isFirstChallenge = challenge.index === 0;
  const estimatedTime = isFirstChallenge && median_starttime ? median_starttime : 0;
  const [timeLeft, setTimeLeft] = useState<number | null>(null);
  const [progress, setProgress] = useState(0);

  const { loading, data, error, stopPolling } = useTrackAndChallengeStatusQuery({
    pollInterval: calculatePollInterval(Date.now() - new Date(track.creating).getTime(), track.median_starttime),
    fetchPolicy: 'network-only',
    variables: {
      trackID: track.id,
      challengeID: challenge.id,
      userID,
      inviteID,
    },
    onCompleted(data) {
      if (data.challenge?.status === ChallengeStatus.Locked) {
        trackAmplitudeEvent({ name: 'Lab - Challenge - Error Challenge is locked' });
        dispatch('error', 'Error', 'Challenge is locked', 0);
      }
      // Set the time left after the first fetching the track status
      if (data.track?.creating && timeLeft === null) {
        setTimeLeft(estimatedTime - differenceInSeconds(new Date().getTime(), new Date(data.track.creating).getTime()));
      }
    },
  });
  const [setupChallenge, { data: challengeInitialized, loading: initializingChallenge }] = useSetupChallengeMutation();

  const trackStatus = data?.track?.status;
  const trackReady = trackStatus === 'created' || trackStatus === 'started';
  const trackFailed = trackStatus === 'failed';
  const challengeStatus = data?.challenge?.status;
  const challengeReady = challengeStatus === 'created' || challengeStatus === 'started';
  const challengeFailed = challengeStatus === 'failed';
  const challengeLocked = challengeStatus === 'locked';
  const failed = trackFailed || challengeFailed || challengeLocked || error;

  useEffect(() => {
    if (!timeLeft || timeLeft <= 0) {
      return;
    }

    const timer = setInterval(() => setTimeLeft(t => t && t - 1), 1000);
    return () => clearInterval(timer);
  }, [timeLeft]);

  useEffect(() => {
    if (!trackReady || !challengeReady) {
      // Maximum value would be 95. The rest would be filled after the challenge is ready
      setProgress(Math.floor(((estimatedTime - Number(timeLeft)) / estimatedTime) * 95));
    } else {
      setProgress(100);
    }
  }, [challengeReady, estimatedTime, timeLeft, trackReady]);

  // TODO: IN-601 Challenge setup should happen automatic, always.
  useEffect(() => {
    if (trackReady && challengeStatus === 'unlocked' && !challengeInitialized && !initializingChallenge) {
      setupChallenge({ variables: { challengeID: challenge.id } }).catch(() =>
        dispatch(
          'error',
          'Failed to setup challenge',
          'Something went wrong while setting up the challenge, please try again.',
          10,
        ),
      );
    }
  }, [
    challenge.id,
    setupChallenge,
    initializingChallenge,
    trackReady,
    challengeStatus,
    challengeInitialized,
    dispatch,
  ]);

  useEffect(() => {
    if ((trackReady && challengeReady) || failed) {
      stopPolling();
    }
  }, [challengeReady, failed, stopPolling, trackReady]);

  useEffect(() => {
    if (error) {
      dispatch('error', 'Failed to fetch status', error.message, 10);
    }
  }, [dispatch, error, stopPolling]);

  useEffect(() => {
    switch (trackStatus) {
      case TrackStatus.Failed:
      case TrackStatus.Stopped:
        dispatch('error', 'Failed to start track', 'Unable to start track, please try again', 10);
    }
  }, [dispatch, trackStatus]);

  useEffect(() => {
    switch (challengeStatus) {
      case ChallengeStatus.Failed:
        dispatch(
          'error',
          'Error setting up challenge',
          data?.challenge?.failMessage ?? 'Something went wrong while setting up the challenge, please try again.',
          10,
        );
    }
  }, [challengeStatus, data?.challenge?.failMessage, dispatch]);

  if (!data && loading) {
    return null;
  }

  const showStartButton =
    trackReady && challengeStatus && [ChallengeStatus.Created, ChallengeStatus.Started].includes(challengeStatus);

  return (
    <>
      {showStartButton && (
        <Button disabled={starting} onClick={onClickStart}>
          {starting ? 'Starting...' : 'Start'}
        </Button>
      )}

      {!failed && (!trackReady || !challengeReady) && (
        <TrackLoadingIndicator
          loading={!trackReady || !challengeReady}
          title={<LoadingMessage />}
          subtitle={isFirstChallenge && timeLeft ? <LoadingCounter timeLeft={timeLeft} /> : 'Starting soon...'}
        />
      )}

      {isFirstChallenge && <LoadingProgressBar value={progress} tw="w-[100vw] absolute bottom-0 left-0" />}
    </>
  );
}

// Method to calculate the poll interval for graphql.
function calculatePollInterval(elapsed: number, avg: number | null): number {
  // If there is no average yet, return 2 seconds.
  if (!avg) {
    return DEFAULT_POLL_INTERVAL;
  }

  // Calculate the max waiting
  const max_wait = Math.min(10, Math.round(avg / 2));

  const percentage = 1 - elapsed / (avg * 1000);

  // Calculate the current amount of seconds based on the elapsed time and the average starttime
  const wait = Math.round(max_wait * percentage);

  // Get the max of wait and 2 so the interval doesn't get lower than 2
  return Math.max(wait, 2) * 1000;
}
